<template>
  <div>
    <div v-for="line in lines" :key="line.key" class="mb-6">
      <h4 class="text-subtitle-1 font-weight-bold mb-2">{{ line.label }} · uso por servicio</h4>
      <p v-if="!line.services.length" class="text-body-2">Sin datos para esta línea.</p>
      <v-simple-table v-else dense class="actuarial__table">
        <thead>
          <tr>
            <th>Servicio</th>
            <th class="text-right">Mascota-años</th>
            <th class="text-right">Eventos</th>
            <th class="text-right">Frecuencia al año</th>
            <th class="text-right">1º evento</th>
            <th class="text-right">2º evento</th>
            <th class="text-right">3º evento</th>
            <th class="text-right">Uso de lo disponible</th>
            <th>Origen</th>
            <th>Confianza</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in line.services" :key="row.code">
            <td>{{ serviceName(row.code) }}</td>
            <td class="text-right">{{ num(row.exposurePetYears) }}</td>
            <td class="text-right">{{ num(row.events) }}</td>
            <td class="text-right">{{ num(row.frequency, 3) }}</td>
            <td v-for="k in [0, 1, 2]" :key="k" class="text-right" :class="{ 'actuarial__borrowed': !row.kthEvent[k].measured }">
              <span :title="row.kthEvent[k].offered === false ? 'Ningún plan lo ofrece hoy: es una proyección' : ''">
                {{ pct(row.kthEvent[k].probability, 1) }}
              </span>
            </td>
            <td class="text-right">{{ pct(row.utilizationOfAvailable) }}</td>
            <td>{{ sourceLabel(row.source) }}</td>
            <td>{{ row.confidence }}</td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <p class="text-caption">
      Probabilidad de usar el 1º, 2º o 3º evento en un año de cobertura, entre las mascotas que lo tenían disponible. En gris: no
      medido en este segmento: proyección con datos de otro segmento o con el supuesto. Si ningún plan ofrece ese evento, la
      proyección solo se usa al cotizar un plan a medida que lo incluya.
    </p>

    <template v-if="petsPerUser.length">
      <h4 class="text-subtitle-1 font-weight-bold mt-6 mb-2">Employee Benefit · mascotas por colaborador que activa</h4>
      <v-simple-table dense class="actuarial__table">
        <thead>
          <tr>
            <th>Mascotas permitidas por colaborador</th>
            <th class="text-right">Mascotas por colaborador que activa</th>
            <th class="text-right">Contratos</th>
            <th>Origen</th>
            <th>Confianza</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(point, i) in petsPerUser" :key="point.limit">
            <td>{{ i === petsPerUser.length - 1 ? `${point.limit} o más` : point.limit }}</td>
            <td class="text-right">{{ num(point.petsPerUser, 2) }}</td>
            <td class="text-right">
              <a v-if="point.contractList && point.contractList.length" class="actuarial__link" @click="contractsOf = point">
                {{ num(point.contracts) }}
              </a>
              <span v-else>{{ num(point.contracts) }}</span>
            </td>
            <td>{{ sourceLabel(point.source) }}</td>
            <td>{{ point.confidence }}</td>
          </tr>
        </tbody>
      </v-simple-table>
      <p class="text-caption">
        Permitir más mascotas por colaborador sube las mascotas activas, pero no en proporción: la mayoría tiene una. El cotizador
        usa esta curva para el costo cuando se venden 2 o más membresías por colaborador. Pincha los contratos para ver cuáles son.
      </p>
      <v-dialog :value="Boolean(contractsOf)" max-width="900" @input="contractsOf = null">
        <v-card v-if="contractsOf">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Contratos con {{ contractsLabel(contractsOf) }} por colaborador
          </v-card-title>
          <v-card-text>
            <v-simple-table dense class="actuarial__table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Contrato</th>
                  <th class="text-right">Permitidas</th>
                  <th class="text-right" title="Promedio de colaboradores con al menos una mascota activa, en los meses de la ventana">Activan</th>
                  <th class="text-right">Mascotas por colaborador</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in contractsOf.contractList" :key="c.contratoId">
                  <td>{{ c.company }}</td>
                  <td>{{ c.code || '—' }}</td>
                  <td class="text-right">{{ c.limit >= 1000 ? 'Sin límite' : c.limit }}</td>
                  <td class="text-right">{{ num(c.users) }}</td>
                  <td class="text-right">{{ num(c.petsPerUser, 2) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <p class="text-caption mt-2 mb-0">
              Últimos 12 meses de la foto. El valor de la curva pondera cada contrato por sus colaboradores que activan y se acerca
              a 1 mascota (límite 1) o al promedio de los contratos con 2 o más cuando hay pocos contratos; nunca supera lo permitido.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="contractsOf = null">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <h4 class="text-subtitle-1 font-weight-bold mt-6 mb-2">Pago esperado por evento según tope</h4>
    <v-simple-table dense class="actuarial__table">
      <thead>
        <tr>
          <th>Servicio</th>
          <th class="text-right">Montos</th>
          <th v-for="cap in shownCaps" :key="cap" class="text-right">Tope {{ money(cap) }}</th>
          <th>Origen</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in severityRows" :key="row.code">
          <td>{{ serviceName(row.code) }}</td>
          <td class="text-right">{{ num(row.sample) }} ({{ row.windowMonths }}m)</td>
          <td v-for="cap in shownCaps" :key="cap" class="text-right">
            {{ money(payoutAt(row, cap)) }}
            <div class="text-caption">{{ pct(shareAboveAt(row, cap)) }} sobre</div>
          </td>
          <td>{{ sourceLabel(row.source) }}</td>
        </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import { formatMoney } from '../utils/b2b-quotes.utils';
import { LINE_LABELS, num, pct, serviceName, SOURCE_LABELS } from '../utils/actuarial.utils';

/** Caps shown of the severity curve, in CLP; other countries use the curve's own points. */
const SHOWN_CAPS_CLP = [10000, 20000, 50000, 200000, 500000, 1000000];

/** Usage by service and severity by cap of one country (aggregates only). */
export default {
  name: 'ActuarialServices',
  props: {
    model: { type: Object, required: true },
    currency: { type: String, default: 'CLP' }
  },
  data() {
    return { contractsOf: null };
  },
  computed: {
    lines() {
      return Object.entries(LINE_LABELS).map(([key, label]) => ({
        key,
        label,
        services: Object.entries(this.model.lines[key]?.services ?? {})
          .map(([code, usage]) => ({ code, ...usage }))
          .sort((a, b) => b.events - a.events)
      }));
    },
    petsPerUser() {
      return this.model.lines.employee_benefit?.petsPerUser ?? [];
    },
    severityRows() {
      return Object.entries(this.model.severity ?? {}).map(([code, curve]) => ({ code, ...curve }));
    },
    shownCaps() {
      const first = this.severityRows[0];
      if (!first) return [];
      const ratio = first.points[first.points.length - 1].cap / 2000000;
      return SHOWN_CAPS_CLP.map((cap) => cap * ratio);
    }
  },
  methods: {
    pointAt(row, cap) {
      return row.points.reduce((best, p) => (Math.abs(p.cap - cap) < Math.abs(best.cap - cap) ? p : best), row.points[0]);
    },
    payoutAt(row, cap) {
      return this.pointAt(row, cap).expectedPayout;
    },
    shareAboveAt(row, cap) {
      return this.pointAt(row, cap).shareAbove;
    },
    money(value) {
      return formatMoney(value, this.currency);
    },
    contractsLabel(point) {
      const last = this.petsPerUser[this.petsPerUser.length - 1];
      return point.limit === last.limit ? `${point.limit} o más mascotas` : `${point.limit} ${point.limit === 1 ? 'mascota' : 'mascotas'}`;
    },
    serviceName,
    pct,
    num,
    sourceLabel(source) {
      return SOURCE_LABELS[source] ?? source;
    }
  }
};
</script>

<style scoped>
.actuarial__table {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
}
.actuarial__link {
  cursor: pointer;
  text-decoration: underline;
}
.actuarial__borrowed {
  color: var(--pw-text-faint);
}
</style>
